import { useState, useEffect } from "react";
import { benchmarksApi } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Loader2, Target, Save, RefreshCw } from "lucide-react";

const BenchmarkSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [benchmarks, setBenchmarks] = useState({
    billable_target: 65,
    realization_target: 90,
    bill_to_pay_target: 225,
    partner_billable_target: 50,
    partner_realization_target: 90,
    partner_bill_to_pay_target: 100,
  });
  const [originalBenchmarks, setOriginalBenchmarks] = useState({});

  useEffect(() => {
    loadBenchmarks();
  }, []);

  const loadBenchmarks = async () => {
    try {
      const response = await benchmarksApi.get();
      setBenchmarks(response.data);
      setOriginalBenchmarks(response.data);
    } catch (error) {
      toast.error("Failed to load benchmarks");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await benchmarksApi.update({
        billable_target: parseFloat(benchmarks.billable_target),
        realization_target: parseFloat(benchmarks.realization_target),
        bill_to_pay_target: parseFloat(benchmarks.bill_to_pay_target),
        partner_billable_target: parseFloat(benchmarks.partner_billable_target),
        partner_realization_target: parseFloat(benchmarks.partner_realization_target),
        partner_bill_to_pay_target: parseFloat(benchmarks.partner_bill_to_pay_target),
      });
      setBenchmarks(response.data);
      setOriginalBenchmarks(response.data);
      toast.success("Benchmarks updated successfully");
    } catch (error) {
      toast.error("Failed to update benchmarks");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setBenchmarks(originalBenchmarks);
    toast.info("Changes discarded");
  };

  const hasChanges =
    benchmarks.billable_target !== originalBenchmarks.billable_target ||
    benchmarks.realization_target !== originalBenchmarks.realization_target ||
    benchmarks.bill_to_pay_target !== originalBenchmarks.bill_to_pay_target ||
    benchmarks.partner_billable_target !== originalBenchmarks.partner_billable_target ||
    benchmarks.partner_realization_target !== originalBenchmarks.partner_realization_target ||
    benchmarks.partner_bill_to_pay_target !== originalBenchmarks.partner_bill_to_pay_target;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading-spinner">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in max-w-2xl" data-testid="benchmark-settings">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-foreground">Benchmark Settings</h1>
        <p className="text-muted-foreground mt-1">
          Configure firm-wide performance targets
        </p>
      </div>

      {/* Benchmarks card */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Target className="text-blue-500" size={20} />
            <CardTitle>Performance Benchmarks</CardTitle>
          </div>
          <CardDescription>
            These targets are used to measure employee performance across the firm.
            Employees see their stats compared against these benchmarks.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Billable % */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label htmlFor="billable" className="text-base font-medium">
                Billable Hours Target
              </Label>
              <span className="text-sm text-muted-foreground">
                Current: {benchmarks.billable_target}%
              </span>
            </div>
            <div className="flex items-center gap-4">
              <Input
                id="billable"
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={benchmarks.billable_target}
                onChange={(e) =>
                  setBenchmarks({
                    ...benchmarks,
                    billable_target: e.target.value,
                  })
                }
                className="w-32"
                data-testid="input-billable-target"
              />
              <span className="text-muted-foreground">%</span>
            </div>
            <p className="text-sm text-muted-foreground">
              Percentage of total hours that should be billable to clients.
            </p>
          </div>

          {/* Realization */}
          <div className="space-y-3 pt-4 border-t border-border">
            <div className="flex items-center justify-between">
              <Label htmlFor="realization" className="text-base font-medium">
                Client Realization Target
              </Label>
              <span className="text-sm text-muted-foreground">
                Current: {benchmarks.realization_target}%
              </span>
            </div>
            <div className="flex items-center gap-4">
              <Input
                id="realization"
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={benchmarks.realization_target}
                onChange={(e) =>
                  setBenchmarks({
                    ...benchmarks,
                    realization_target: e.target.value,
                  })
                }
                className="w-32"
                data-testid="input-realization-target"
              />
              <span className="text-muted-foreground">%</span>
            </div>
            <p className="text-sm text-muted-foreground">
              Percentage of billed amount that should be collected (realized).
            </p>
          </div>

          {/* Bill to Pay */}
          <div className="space-y-3 pt-4 border-t border-border">
            <div className="flex items-center justify-between">
              <Label htmlFor="billToPay" className="text-base font-medium">
                Bill to Pay Target
              </Label>
              <span className="text-sm text-muted-foreground">
                Current: {benchmarks.bill_to_pay_target}%
              </span>
            </div>
            <div className="flex items-center gap-4">
              <Input
                id="billToPay"
                type="number"
                step="1"
                min="0"
                max="500"
                value={benchmarks.bill_to_pay_target}
                onChange={(e) =>
                  setBenchmarks({
                    ...benchmarks,
                    bill_to_pay_target: e.target.value,
                  })
                }
                className="w-32"
                data-testid="input-bill-to-pay-target"
              />
              <span className="text-muted-foreground">%</span>
            </div>
            <p className="text-sm text-muted-foreground">
              Total amount billed as a percentage of employee's total pay.
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-6 border-t border-border">
            {hasChanges && (
              <Button
                variant="outline"
                onClick={handleReset}
                className="gap-2"
                data-testid="reset-benchmarks-btn"
              >
                <RefreshCw size={16} />
                Discard Changes
              </Button>
            )}
            <Button
              onClick={handleSave}
              disabled={saving || !hasChanges}
              className="gap-2 bg-blue-600 hover:bg-blue-700"
              data-testid="save-benchmarks-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save size={16} />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Partner Benchmarks card */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Target className="text-purple-500" size={20} />
            <CardTitle>Partner Benchmarks</CardTitle>
          </div>
          <CardDescription>
            Partners have different performance targets than staff employees.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Partner Billable % */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label htmlFor="partner_billable" className="text-base font-medium">
                Partner Billable Hours Target
              </Label>
              <span className="text-sm text-muted-foreground">
                Current: {benchmarks.partner_billable_target || 50}%
              </span>
            </div>
            <div className="flex items-center gap-4">
              <Input
                id="partner_billable"
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={benchmarks.partner_billable_target || 50}
                onChange={(e) =>
                  setBenchmarks({
                    ...benchmarks,
                    partner_billable_target: e.target.value,
                  })
                }
                className="w-32"
                data-testid="input-partner-billable-target"
              />
              <span className="text-muted-foreground">%</span>
            </div>
          </div>

          {/* Partner Realization */}
          <div className="space-y-3 pt-4 border-t border-border">
            <div className="flex items-center justify-between">
              <Label htmlFor="partner_realization" className="text-base font-medium">
                Partner Realization Target
              </Label>
              <span className="text-sm text-muted-foreground">
                Current: {benchmarks.partner_realization_target || 90}%
              </span>
            </div>
            <div className="flex items-center gap-4">
              <Input
                id="partner_realization"
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={benchmarks.partner_realization_target || 90}
                onChange={(e) =>
                  setBenchmarks({
                    ...benchmarks,
                    partner_realization_target: e.target.value,
                  })
                }
                className="w-32"
                data-testid="input-partner-realization-target"
              />
              <span className="text-muted-foreground">%</span>
            </div>
          </div>

          {/* Partner Bill to Pay */}
          <div className="space-y-3 pt-4 border-t border-border">
            <div className="flex items-center justify-between">
              <Label htmlFor="partner_billToPay" className="text-base font-medium">
                Partner Bill to Pay Target
              </Label>
              <span className="text-sm text-muted-foreground">
                Current: {benchmarks.partner_bill_to_pay_target || 100}%
              </span>
            </div>
            <div className="flex items-center gap-4">
              <Input
                id="partner_billToPay"
                type="number"
                step="1"
                min="0"
                max="500"
                value={benchmarks.partner_bill_to_pay_target || 100}
                onChange={(e) =>
                  setBenchmarks({
                    ...benchmarks,
                    partner_bill_to_pay_target: e.target.value,
                  })
                }
                className="w-32"
                data-testid="input-partner-bill-to-pay-target"
              />
              <span className="text-muted-foreground">%</span>
            </div>
            <p className="text-sm text-muted-foreground">
              Partners typically have a 1:1 (100%) bill to pay target.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Info card */}
      <Card className="bg-blue-500/10 border-blue-100">
        <CardContent className="pt-6">
          <div className="flex gap-4">
            <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0">
              <Target className="text-blue-500" size={20} />
            </div>
            <div>
              <h3 className="font-medium text-foreground mb-1">
                How benchmarks work
              </h3>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>
                  • <strong>Billable %:</strong> Billable Hours ÷ Total Hours × 100
                </li>
                <li>
                  • <strong>Realization:</strong> Realized Amount ÷ Billed Amount × 100
                </li>
                <li>
                  • <strong>Bill to Pay:</strong> Billed Amount ÷ Total Pay × 100
                </li>
              </ul>
              <p className="text-sm text-muted-foreground mt-2">
                Employees see color-coded indicators: green (meets target), amber (close),
                or red (below target).
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BenchmarkSettings;
